import {
  addProps,
  addHandlers,
  addDisplayName,
  flowMax,
  addState,
} from 'ad-hok'
import { FC } from 'react'
import { addApi, addUser } from 'shared/src/api'
import { addNavigate } from 'shared/src/utils/addNavigate'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { SocialService } from '../utils/social'
import { NextActionButton } from './NextActionButton'

interface Props {
  service: SocialService
  disableUnlinking?: boolean
  onClick?: () => void
  className?: string
}

export const SocialLinkButton: FC<Props> = flowMax(
  addDisplayName('SocialLinkButton'),
  addUser,
  addProps(({ service, user }) => ({
    linked:
      service === 'instagram'
        ? user.hasInstagramConnection
        : user.hasTikTokConnection,
  })),
  addApi,
  addState('inProgress', 'setInProgress', false),
  addNavigate,
  addHandlers({
    onClick:
      ({
        user,
        service,
        linked,
        api,
        setInProgress,
        refetchUser,
        onClick,
        navigate,
      }) =>
      async () => {
        onClick?.()

        setInProgress(true)

        try {
          if (linked) {
            await api.users[service].unlink()
            refetchUser()
          } else {
            if (service === 'instagram' && user.shouldScrapeInstagram) {
              navigate('/account/linkInstagram')
            } else {
              const { url } = await api.users[service].getLoginUrl()
              window.location.href = url
            }
          }
        } catch {
          const message = linked
            ? `Error unlinking ${service}`
            : `Error getting redirect URL for ${service}`
          console.error(message)
        }

        setInProgress(false)
      },
  }),
  addTranslationHelpers,
  addProps(({ service, linked, disableUnlinking, t }) => ({
    title: t(
      linked && disableUnlinking
        ? 'admin.resources.queue.linked'
        : linked
        ? 'admin.resources.queue.unlink'
        : 'admin.resources.queue.link',
      { service: t(`admin.resources.queue.services.${service}`) }
    ),
  })),
  ({
    service,
    linked,
    disableUnlinking,
    onClick,
    title,
    inProgress,
    className,
  }) => (
    <NextActionButton
      icon={service}
      rightIcon={linked && disableUnlinking ? 'check' : undefined}
      text={title}
      onClick={onClick}
      disabled={inProgress || (linked && disableUnlinking)}
      filled={linked}
      className={className}
    />
  )
)
